import {type Descendant} from 'slate';
import {create} from 'zustand';
import {createJSONStorage, persist} from 'zustand/middleware';

import {safeLocalStorage} from '../../util/localStorage';
import {
  COMPOSER_INITIAL_VALUE,
  DEFAULT_DRAWER_WIDTH,
  DEFAULT_WINDOW_HEIGHT,
  DEFAULT_WINDOW_WIDTH,
} from './constants';

const wbAgentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: name => safeLocalStorage.getItem(name),
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch (e) {
      if (
        e instanceof DOMException &&
        (e.code === 22 || e.name === 'QuotaExceededError')
      ) {
        console.warn(`localStorage quota exceeded for "${name}"`, e);
        return;
      }
      console.warn(`Failed to persist "${name}" to localStorage`, e);
    }
  },
  removeItem: name => safeLocalStorage.removeItem(name),
};

export interface SelectedRun {
  name: string;
  displayName: string;
}

interface WindowPosition {
  x: number;
  y: number;
}

interface WBAgentContextState {
  // Agent chat open state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Docked state
  docked: boolean;
  setDocked: (docked: boolean) => void;

  // Drawer width
  drawerWidth: number;
  setDrawerWidth: (width: number) => void;

  // Window geometry (persists across open/close and dock/undock)
  windowPosition: WindowPosition | null;
  setWindowPosition: (pos: WindowPosition) => void;
  windowWidth: number;
  windowHeight: number;
  setWindowSize: (width: number, height: number) => void;

  // Composer contents
  composerDraft: Descendant[];
  setComposerDraft: (value: Descendant[]) => void;

  // Run context
  selectedRuns: SelectedRun[];
  projectName: string | undefined;
  addRun: (run: SelectedRun) => void;
  removeRun: (name: string) => void;
  clearRuns: () => void;
  syncProject: (projectName: string | undefined) => void;
}

export const useWBAgentContextStore = create<WBAgentContextState>()(
  persist(
    set => ({
      open: false,
      setOpen: open => set({open}),
      docked: true,
      setDocked: docked => set({docked}),
      drawerWidth: DEFAULT_DRAWER_WIDTH,
      setDrawerWidth: width => set({drawerWidth: width}),
      windowPosition: null,
      setWindowPosition: pos => set({windowPosition: pos}),
      windowWidth: DEFAULT_WINDOW_WIDTH,
      windowHeight: DEFAULT_WINDOW_HEIGHT,
      setWindowSize: (width, height) =>
        set({windowWidth: width, windowHeight: height}),
      composerDraft: COMPOSER_INITIAL_VALUE,
      setComposerDraft: value => set({composerDraft: value}),
      selectedRuns: [],
      projectName: undefined,
      addRun: run =>
        set(state => {
          if (state.selectedRuns.some(r => r.name === run.name)) {
            return state;
          }
          return {selectedRuns: [...state.selectedRuns, run]};
        }),
      removeRun: name =>
        set(state => ({
          selectedRuns: state.selectedRuns.filter(r => r.name !== name),
        })),
      clearRuns: () => set({selectedRuns: []}),
      syncProject: projectName =>
        set(state => {
          if (state.projectName === projectName) {
            return state;
          }
          return {projectName, selectedRuns: []};
        }),
    }),
    {
      name: 'wb-agent-window-preferences',
      storage: createJSONStorage(() => wbAgentStorage),
      partialize: state => ({
        open: state.open,
        docked: state.docked,
        drawerWidth: state.drawerWidth,
        windowPosition: state.windowPosition,
        windowWidth: state.windowWidth,
        windowHeight: state.windowHeight,
      }),
    }
  )
);
